import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { swal, typeChecker } from '../../../utils/methods';
import { _post } from '../../../utils/network';
import { contentLevelOptions, fetchBlogCategoryOptions, isValidSlug, shamsiToGregorian, slugify, toShamsiDate } from '../blogUtils';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from '../../../components/Form/FormField/FormField.component';
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';
import ContentEditor from '../../../components/Form/ContentEditor/ContentEditor.component';

const SECTION_ID_PREFIX = 'blog-post-content-';

const stripHtml = html => html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

const nextSectionNumber = sections => sections.reduce((max, section) => {
	const number = parseInt(String(section.id || '').replace(SECTION_ID_PREFIX, ''), 10);
	return Number.isNaN(number) ? max : Math.max(max, number);
}, 0) + 1;

const BlogPostsCreate = ({ componentType='create' }) => {
	const { t } = useTranslation();
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const formRef = useRef(null);
	const sectionEditorRef = useRef(null);
	const slugTouchedRef = useRef(false);
	const [formKey, setFormKey] = useState(0);
	const [saveButtonIsLoading, setSaveButtonIsLoading] = useState(false);
	const [deleteButtonIsLoading, setDeleteButtonIsLoading] = useState(false);
	const [blogPost, setBlogPost] = useState({});
	const [blogCategories, setBlogCategories] = useState([]);
	const [sections, setSections] = useState([]);
	const [sectionTitle, setSectionTitle] = useState('');
	const [sectionContent, setSectionContent] = useState('');
	const [editingSectionId, setEditingSectionId] = useState(null);
	const [editorKey, setEditorKey] = useState(0);

	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('blog_posts'), link: '/blog-posts-list' },
		{ title: t(componentType) }
	];
	const confirmSwal = statements => {
		swal(() => statements(), {
			text: t('are_you_sure_to_do_this'),
			confirmButtonText: t('yes'),
			cancelButtonText: t('no')
		});
	};

	const clearSectionEditor = () => {
		setSectionTitle('');
		setSectionContent('');
		setEditingSectionId(null);
		setEditorKey(key => key + 1);
	};

	const resetCreateForm = () => {
		slugTouchedRef.current = false;
		setSections([]);
		clearSectionEditor();
		setFormKey(key => key + 1);
	};

	const saveSection = () => {
		if(!typeChecker(sectionTitle.trim(), 'title'))
			return toast.warn(t('blog_section_title') + ' ' + t('must_have_at_least_3_characters'));
		if(!stripHtml(sectionContent) && !/<img/i.test(sectionContent))
			return toast.warn(t('blog_section_body_required'));
		if(editingSectionId !== null)
			setSections(sections.map(section => section.id === editingSectionId ? { ...section, title: sectionTitle.trim(), content: sectionContent } : section));
		else
			setSections([...sections, { id: SECTION_ID_PREFIX + nextSectionNumber(sections), title: sectionTitle.trim(), content: sectionContent }]);
		clearSectionEditor();
	};

	const editSection = sectionId => {
		const section = sections.find(item => item.id === sectionId);
		if(!section)
			return;
		setSectionTitle(section.title);
		setSectionContent(section.content);
		setEditingSectionId(sectionId);
		setEditorKey(key => key + 1);
		sectionEditorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	const deleteSection = sectionId => {
		confirmSwal(() => {
			setSections(current => current.filter(section => section.id !== sectionId));
			if(editingSectionId === sectionId)
				clearSectionEditor();
		});
	};

	const moveSection = (index, step) => {
		const target = index + step;
		if(target < 0 || target >= sections.length)
			return;
		const reordered = [...sections];
		[reordered[index], reordered[target]] = [reordered[target], reordered[index]];
		setSections(reordered);
	};

	const onTitleChange = event => {
		if(componentType !== 'create' || slugTouchedRef.current || !formRef.current)
			return;
		formRef.current.blogPostSlug.value = slugify(event.target.value);
	};

	const saveBlogPost = event => {
		event.preventDefault();
		if(saveButtonIsLoading)
			return;
		const form = event.target;
		const title = form.blogPostTitle.value.trim();
		if(!typeChecker(title, 'title'))
			return toast.warn(t('title') + ' ' + t('must_have_at_least_3_characters'));
		const slug = form.blogPostSlug.value.trim();
		if(!isValidSlug(slug))
			return toast.warn(t('slug_is_not_valid'));
		const categoryId = form.blogPostCategory.value;
		if(!typeChecker(categoryId, 'uuid'))
			return toast.warn(t('category') + ' ' + t('cant_be_empty'));
		const brief = form.blogPostBrief.value.trim();
		if(!typeChecker(brief, 'title'))
			return toast.warn(t('brief') + ' ' + t('must_have_at_least_3_characters'));
		const image = document.querySelector('#blogPostImage-upload-list img')?.getAttribute('src')?.trim() || '';
		if(!typeChecker(image, 'image'))
			return toast.warn(t('image') + ' ' + t('cant_be_empty'));
		const readingDuration = form.blogPostReadingDuration.value.trim();
		if(!readingDuration)
			return toast.warn(t('reading_duration') + ' ' + t('cant_be_empty'));
		const publishesAt = form.blogPostPublishesAt.value.trim();
		if(!typeChecker(publishesAt, 'shamsi-date'))
			return toast.warn(t('publishes_at') + ': ' + t('date_is_not_valid'));
		if(sections.length === 0)
			return toast.warn(t('blog_at_least_one_section'));
		if(editingSectionId !== null || sectionTitle.trim() || stripHtml(sectionContent))
			return toast.warn(t('blog_unsaved_section'));

		const body = {
			id,
			title,
			slug,
			category_id: categoryId,
			brief,
			images: { ...(blogPost?.images || {}), image },
			contents: sections,
			reading_duration: readingDuration,
			level: form.blogPostLevel.value || 'beginner',
			is_special: form.blogPostIsSpecial.checked,
			is_active: form.blogPostIsActive.checked,
			publishes_at: shamsiToGregorian(publishesAt),
			more: blogPost?.more || {}
		};
		confirmSwal(async () => {
			setSaveButtonIsLoading(true);
			const response = await _post(`/blog_posts/admin/${componentType === 'create' ? 'create' : 'update'}BlogPost`, body);
			setSaveButtonIsLoading(false);
			switch(response.code){
				case 200:
					toast.success(response[t('api_message')]);
					if(componentType === 'create')
						resetCreateForm();
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	};

	const deleteBlogPost = () => {
		if(deleteButtonIsLoading)
			return;
		confirmSwal(async () => {
			setDeleteButtonIsLoading(true);
			const response = await _post('/blog_posts/admin/deleteBlogPost', { id });
			setDeleteButtonIsLoading(false);
			switch(response.code){
				case 200:
					toast.success(response[t('api_message')]);
					navigate('/blog-posts-list');
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	};

	const getBlogCategories = async () => {
		const { options, response } = await fetchBlogCategoryOptions();
		if(response.code !== 200)
			return toast.error(response[t('api_message')]);
		setBlogCategories(options);
	};

	const getBlogPost = async () => {
		const response = await _post('/blog_posts/admin/getOneBlogPost', { id });
		switch(response.code){
			case 200:
				setBlogPost(response.data.blog_post);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	useEffect(() => {
		getBlogCategories();
		if(componentType === 'edit')
			getBlogPost();
	}, []);
	useEffect(() => {
		if(componentType === 'create')
			resetCreateForm();
	}, [location]);
	useEffect(() => {
		if(!Array.isArray(blogPost?.contents))
			return;
		let counter = nextSectionNumber(blogPost.contents);
		setSections(blogPost.contents.map(section => section.id ? section : { ...section, id: SECTION_ID_PREFIX + counter++ }));
	}, [blogPost]);

	const publishesAtValue = componentType === 'edit' ? toShamsiDate(blogPost?.publishes_at) : toShamsiDate(new Date().toISOString());

	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t(componentType)} {t('blog_post')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={saveBlogPost} ref={formRef} key={formKey}>
						<div className="row gx-2">
							<FormField fieldKey="blogPostTitle" title={t('title')} value={blogPost?.title} onChange={onTitleChange} required />
							<div className="col-md-6 mb-3">
								<label className="form-label" htmlFor="blogPostSlug">
									{t('slug')} <span className="text-danger"> *</span>
								</label>
								<input className="form-control ltr" id="blogPostSlug" name="blogPostSlug" type="text" defaultValue={blogPost?.slug} placeholder={t('slug')} onChange={() => { slugTouchedRef.current = true; }} />
							</div>
							<FormField type="select" fieldKey="blogPostCategory" title={t('category')} value={blogPost?.category_id} selectItems={blogCategories} required />
							<FormField fieldKey="blogPostReadingDuration" title={t('reading_duration')} tip={t('reading_duration_tip')} value={blogPost?.reading_duration} required />
							<FormField type="select" fieldKey="blogPostLevel" title={t('level')} value={blogPost?.level || 'beginner'} selectItems={contentLevelOptions(t)} required />
							<FormField fieldKey="blogPostPublishesAt" title={t('publishes_at')} tip={t('publishes_at_tip')} value={publishesAtValue} required ltr />
							<FormField type="toggle" fieldKey="blogPostIsActive" title={t('active')} value={blogPost?.is_active} required />
							<FormField type="toggle" fieldKey="blogPostIsSpecial" title={t('special')} value={blogPost?.is_special} required />
							<FormField type="text" fieldKey="blogPostBrief" title={t('brief')} value={blogPost?.brief} required />
							<FormField type="image" fieldKey="blogPostImage" title={t('image')} value={blogPost?.images?.image} required fileMaxSize={2} imageAspectRatio="16 / 9" />
							{componentType === 'edit' &&
								<>
									<FormField fieldKey="blogPostViews" title={t('views')} value={blogPost?.views_count ?? 0} disabled ltr />
									<FormField fieldKey="blogPostCreatedAt" title={t('creation_date')} value={toShamsiDate(blogPost?.created_at)} disabled ltr />
									<FormField fieldKey="blogPostUpdatedAt" title={t('update_date')} value={toShamsiDate(blogPost?.updated_at)} disabled ltr />
								</>
							}

							<div className="col-12">
								<hr className="my-4" />
							</div>

							<div className="col-12 mb-3" ref={sectionEditorRef}>
								<h6 className="mb-3">
									{t('blog_sections')} {editingSectionId ? <small className="text-warning">({t('editing')})</small> : ''}
								</h6>
								<div className="mb-3">
									<label className="form-label" htmlFor="blogSectionTitle">{t('blog_section_title')}</label>
									<input className="form-control" id="blogSectionTitle" type="text" value={sectionTitle} placeholder={t('blog_section_title')} onChange={event => setSectionTitle(event.target.value)} />
								</div>
								<ContentEditor
									key={`section-editor-${editorKey}`}
									fieldKey="blogSectionBody"
									title={t('blog_section_body')}
									initContent={sectionContent}
									onChange={event => setSectionContent(event.editor.getData())}
								/>
								<div>
									<button type="button" className={`btn ${editingSectionId ? 'btn-primary' : 'btn-success'} me-2`} onClick={saveSection}>
										<i className={`fa ${editingSectionId ? 'fa-save' : 'fa-plus'}`}></i> {editingSectionId ? t('save') : t('blog_add_section')}
									</button>
									{(editingSectionId || sectionTitle || sectionContent) &&
										<button type="button" className="btn btn-secondary" onClick={clearSectionEditor}>
											<i className="fa fa-times"></i> {t('cancel')}
										</button>
									}
								</div>
							</div>

							{sections.length > 0 &&
								<div className="col-12 mb-3">
									<label className="form-label">{t('blog_added_sections')} ({sections.length})</label>
									<div className="list-group">
										{sections.map((section, index) =>
											<div key={section.id} className={`list-group-item${editingSectionId === section.id ? ' list-group-item-warning' : ''}`}>
												<div className="d-flex justify-content-between align-items-start gap-2">
													<div className="flex-grow-1 overflow-hidden">
														<h6 className="mb-1">{index + 1}. {section.title}</h6>
														<p className="mb-0 text-muted small text-truncate">{stripHtml(section.content)}</p>
													</div>
													<div className="d-flex flex-shrink-0 gap-1">
														<button type="button" className="btn btn-sm btn-outline-secondary" disabled={index === 0} onClick={() => moveSection(index, -1)}>
															<i className="fa fa-arrow-up"></i>
														</button>
														<button type="button" className="btn btn-sm btn-outline-secondary" disabled={index === sections.length - 1} onClick={() => moveSection(index, 1)}>
															<i className="fa fa-arrow-down"></i>
														</button>
														<button type="button" className="btn btn-sm btn-warning" onClick={() => editSection(section.id)}>
															<i className="fa fa-edit"></i> {editingSectionId === section.id ? t('editing') : t('edit')}
														</button>
														<button type="button" className="btn btn-sm btn-danger" onClick={() => deleteSection(section.id)}>
															<i className="fa fa-trash"></i> {t('delete')}
														</button>
													</div>
												</div>
											</div>
										)}
									</div>
								</div>
							}
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name={componentType} isLoading={saveButtonIsLoading}>
								<i className="fa fa-save"></i> {t(componentType === 'create' ? 'create' : 'update')}
							</LoadingButton>
							{componentType === 'edit' &&
								<LoadingButton className="btn btn-danger d-inline-block mt-3" onClick={deleteBlogPost} type="button" name="delete" isLoading={deleteButtonIsLoading}>
									<i className="fa fa-trash"></i> {t('delete')}
								</LoadingButton>
							}
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default BlogPostsCreate;
